import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, ShoppingCart } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  processing: "bg-sky-100 text-sky-800",
  completed: "bg-slate-100 text-slate-800",
  cancelled: "bg-red-100 text-red-800",
};

const STATUS_LABEL = {
  pending: "Pendiente", paid: "Pagado", processing: "En proceso",
  completed: "Completado", cancelled: "Cancelado",
};

const EMPTY = {
  customer_name: "", customer_phone: "", product_name: "", quantity: 1,
  payment_method: "", notes: "", status: "pending", payment_status: "unpaid",
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API}/orders`);
      setOrders(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => { setEditing(o.id); setForm({ ...EMPTY, ...o }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, quantity: Number(form.quantity) || 1 };
    if (editing) {
      await authAxios.patch(`${API}/orders/${editing}`, payload);
    } else {
      await authAxios.post(`${API}/orders`, payload);
    }
    setOpen(false);
    fetchOrders();
  };

  const updateStatus = async (order, status) => {
    await authAxios.patch(`${API}/orders/${order.id}`, { status });
    fetchOrders();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    await authAxios.delete(`${API}/orders/${id}`);
    fetchOrders();
  };

  const filtered = statusFilter === "all" ? orders : orders.filter(o => o.status === statusFilter);

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ShoppingCart className="w-7 h-7" /> Pedidos
          </h1>
          <p className="text-slate-500 mt-1">Gestiona todos los pedidos de tus clientes</p>
        </div>
        <Button onClick={openNew} data-testid="order-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Pedido
        </Button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="orders-filters">
        {["all", "pending", "paid", "processing", "completed", "cancelled"].map(s => (
          <Button key={s} size="sm" variant={statusFilter === s ? "default" : "outline"}
            onClick={() => setStatusFilter(s)} data-testid={`orders-filter-${s}`}>
            {s === "all" ? "Todos" : STATUS_LABEL[s]}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="animate-pulse h-40 bg-slate-200 rounded-xl" />
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">
          <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Sin pedidos.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <Card key={o.id} data-testid={`order-card-${o.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold">{o.customer_name || "Sin nombre"}</h3>
                      <Badge className={STATUS_COLORS[o.status] || ""}>{STATUS_LABEL[o.status] || o.status}</Badge>
                      {o.source === "whatsapp_bot" && (
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs">🤖 Bot</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">📱 {o.customer_phone || "—"}</p>
                    <p className="text-sm mt-1">📦 {o.product_name || "—"} × {o.quantity || 1}
                      {o.total ? <span className="ml-2 font-semibold">= ${Number(o.total).toFixed(2)} {o.currency || "USD"}</span> : null}
                    </p>
                    {o.payment_method && <p className="text-sm text-slate-500">💳 {o.payment_method}</p>}
                    {o.notes && <p className="text-xs text-slate-500 mt-1 italic">{o.notes}</p>}
                    <p className="text-xs text-slate-400 mt-2">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col gap-2 min-w-[180px]">
                    <Select value={o.status} onValueChange={v => updateStatus(o, v)}>
                      <SelectTrigger data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(o)} data-testid={`order-edit-${o.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(o.id)} data-testid={`order-delete-${o.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Pedido" : "Nuevo Pedido"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre cliente</Label>
              <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="order-customer-name" />
            </div>
            <div><Label>Teléfono</Label>
              <Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} data-testid="order-customer-phone" />
            </div>
            <div><Label>Producto / Servicio</Label>
              <Input value={form.product_name} onChange={e => setForm({ ...form, product_name: e.target.value })} data-testid="order-product" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cantidad</Label>
                <Input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
              </div>
              <div><Label>Método pago</Label>
                <Input value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} />
              </div>
            </div>
            <div><Label>Estado</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="order-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
